import { useCallback, useEffect, useRef, useState } from 'react'
import {
  moveDownLeft, moveUpLeft, moveLeft, moveDownRight,
  moveUpRight, moveRight, moveUp, moveDown
} from '../lib/behaviours'
import { useKeypress } from './use-keypress'
import { useWorld } from './use-world'

// Returns [position, updateNav].
// Smooth movement: intended position is calculated every animation frame from
// the current key state, NOT on keydown/keyup events.  Keydown events only fire
// at the keyboard repeat rate (~30 Hz); frame-based sampling runs at 60 Hz with
// no initial delay.
export const useNavigate = (initialPosition, config) => {
  const stride = config?.stride ?? 3
  const posRef = useRef(initialPosition)
  const [position, setPosition] = useState(initialPosition)

  const [keys] = useKeypress()
  // Mirror keys into a ref so the frame effect always sees the latest value
  // without needing keys in its dependency array.
  const keysRef = useRef({})
  useEffect(() => { keysRef.current = keys }, [keys])

  const { frame, setTarget } = useWorld()

  useEffect(() => {
    const k = keysRef.current
    const isMoving = k.ArrowDown || k.ArrowUp || k.ArrowLeft || k.ArrowRight
    if (!isMoving) return

    // Clear click-to-move target when player takes over with keyboard
    setTarget(undefined)

    const pos = posRef.current
    let next

    if      (k.ArrowDown && k.ArrowRight) next = moveDownRight(pos, stride)
    else if (k.ArrowDown && k.ArrowLeft)  next = moveDownLeft(pos, stride)
    else if (k.ArrowUp   && k.ArrowLeft)  next = moveUpLeft(pos, stride)
    else if (k.ArrowUp   && k.ArrowRight) next = moveUpRight(pos, stride)
    else if (k.ArrowDown)  next = moveDown(pos, stride)
    else if (k.ArrowLeft)  next = moveLeft(pos, stride)
    else if (k.ArrowRight) next = moveRight(pos, stride)
    else if (k.ArrowUp)    next = moveUp(pos, stride)

    if (next) {
      posRef.current = next
      setPosition(next)
    }
  }, [frame]) // runs every animation frame

  // Called by Player after collision resolution to keep nav in sync
  const updateNav = useCallback((pos) => {
    posRef.current = pos
    setPosition(pos)
  }, [])

  return [position, updateNav, posRef]
}
